import React, { useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Dropdown from 'core/elements/dropdown'
import DropdownField from 'core/components/validatedForm/DropdownField'
import TextField from 'core/components/validatedForm/TextField'
import Theme from 'core/themes/model'
import { noop, stopPropagation } from 'utils/fp'
import { requiredValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import { getPrivateIpValidator } from '../CreateVirtualMachineModal'
import SubnetPicklist from 'openstack/components/networks/network-details/SubnetPicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  portToolTip: {
    height: '16px',
  },
  portSelection: {},
  fixedIPInput: {},
}))

/**
 * Simple Network columns - only Name and Ports (automatic)
 * Used when vmNetworkMode === NetworkMode.SIMPLE
 */
export const getSimpleNetworkColumns = () => [
  {
    id: 'name',
    label: 'Name',
    render: (name: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text variant="body2">{name}</Text>
      </div>
    ),
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'ports',
    label: (
      <PortsHeaderWithTooltip message="Ports are managed automatically on Layer 2 Networks. To configure ports, use a managed network." />
    ),
    Component: () => {
      return (
        <div>
          <Text variant="body2">Automatic</Text>
        </div>
      )
    },
  },
]

/**
 * Ports header with tooltip for Simple Networks
 */
const PortsHeaderWithTooltip = ({ message }: { message: string }) => {
  const classes = useStyles()
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '16px' }}>
      <span>Ports</span>
      <Tooltip className={classes.portToolTip} message={message}>
        <HelpOutlineIcon style={{ fontSize: '16px', color: '#9E9E9E', cursor: 'help' }} />
      </Tooltip>
    </div>
  )
}

interface ManagedNetworkColumnsParams {
  params: {
    selectedPorts: { [networkId: string]: string }
    fixedIPs: { [networkId: string]: string }
    selectedSubnets: { [networkId: string]: any }
  }
  getParamsUpdater: (key: string) => (value: any) => void
}

/**
 * Managed Network columns - Name, Subnets, Ports, Private IP
 * Used when vmNetworkMode === NetworkMode.MANAGED
 */
export const getManagedNetworkColumns = ({
  params,
  getParamsUpdater,
}: ManagedNetworkColumnsParams) => [
  {
    id: 'name',
    label: 'Name',
    render: (name: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text variant="body2">{name}</Text>
      </div>
    ),
  },
  {
    id: 'id',
    label: 'UUID',
    display: false,
  },
  {
    id: 'subnetDetails',
    label: 'Subnet',
    Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => (
      <div onClick={isSelected ? stopPropagation : noop}>
        <SubnetPicklist
          networkId={row.id}
          includeAutomaticOption
          label=""
          name={`subnet-${row.id}`}
          value={params.selectedSubnets[row.id] ?? ''}
          includeNetworkName={false}
          onChange={(subnet: any) =>
            getParamsUpdater('selectedSubnets')({
              ...params.selectedSubnets,
              [row.id]: subnet,
            })
          }
        />
      </div>
    ),
  },
  {
    id: 'ports',
    label: 'Ports',
    Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
      const classes = useStyles()
      const options = row?.availablePorts?.length
        ? [
            // If there are available IP addresses, show the 'Automatic' and 'Private IP' options
            ...(row?.availableIps > 0
              ? [
                  { label: 'Automatic', value: '' },
                  { label: 'Private IP', value: 'fixedIP' },
                ]
              : [
                  {
                    label: 'Select a port (No available IPs)',
                    value: '',
                  },
                ]),
            // If there are available ports, show the ports as options
            ...row?.availablePorts.map((port: any) => {
              const ipAddressString = port.fixed_ips?.map((ip: any) => ip.ip_address)?.join(', ')
              return {
                label: `${ipAddressString} (${port.name || port.id})`,
                value: port.id,
              }
            }),
          ]
        : [
            { label: 'Automatic', value: '' },
            { label: 'Private IP', value: 'fixedIP' },
          ]
      return (
        <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
          <DropdownField
            id={`${row.id}-ports`}
            DropdownComponent={Dropdown}
            name="ports"
            value={params.selectedPorts[row.id] || ''}
            items={options}
            onChange={(value: string) =>
              getParamsUpdater('selectedPorts')({
                ...params.selectedPorts,
                [row.id]: value,
              })
            }
            required={row?.availableIps === 0 && isSelected}
          />
        </div>
      )
    },
  },
  {
    id: 'fixedIP',
    label: 'Private IP',
    Component: ({ row, isSelected }: { row: any; isSelected: boolean }) => {
      const classes = useStyles()
      const [fixedIP, setFixedIP] = useState(params.fixedIPs[row.id])
      const isRequired = params.selectedPorts[row.id] === 'fixedIP'
      const selectedPort = row?.ports?.find((port: any) => port.id === params.selectedPorts[row.id])
      return isRequired ? (
        <div onClick={isSelected ? stopPropagation : noop}>
          <TextField
            id={`${row.id}-fixedIP`}
            placeholder="Enter private IP"
            className={classes.fixedIPInput}
            value={fixedIP}
            onChange={setFixedIP}
            onBlur={() => {
              getParamsUpdater('fixedIPs')({ ...params.fixedIPs, [row.id]: fixedIP })
            }}
            disabled={!isRequired}
            validations={
              isRequired && isSelected
                ? [validIpv4Validator, requiredValidator, getPrivateIpValidator(row)]
                : null
            }
          />
        </div>
      ) : selectedPort ? (
        <div>
          {selectedPort?.fixed_ips?.map((ip: any) => (
            <Text key={ip.ip_address} variant="body2">
              {ip.ip_address}
            </Text>
          ))}
        </div>
      ) : null
    },
  },
  {
    id: 'port_security_enabled',
    label: 'Port Security',
    render: (val: boolean) => (val ? 'Enabled' : 'Disabled'),
    display: false,
  },
  {
    id: 'provider:network_type',
    label: 'Network Type',
    display: false,
  },
  {
    id: 'mtu',
    label: 'MTU',
    display: false,
  },
  {
    id: 'provider:physical_network',
    label: 'Physical Network',
    display: false,
  },
  {
    id: 'router:external',
    label: 'External',
    render: (val: boolean) => (val ? 'Yes' : 'No'),
    display: false,
  },
  {
    id: 'provider:segmentation_id',
    label: 'Segmentation ID',
    render: (val: string | number | null) => (val ? val : 'N/A'),
    display: false,
  },
  {
    id: 'admin_state_up',
    label: 'Admin State',
    render: (val: boolean) => (val ? 'Up' : 'Down'),
    display: false,
  },
  {
    id: 'dns_domain',
    label: 'DNS Zone',
    render: (val: string | null) => (val ? val : '-'),
    display: false,
  },
  {
    id: 'status',
    label: 'Status',
    display: false,
  },
  {
    id: 'availableIps',
    label: 'Available IPs',
    display: false,
  },
  {
    id: 'shared',
    label: 'Shared Tenants',
    display: false,
  },
  {
    id: 'created_at',
    label: 'Created',
    display: false,
  },
]

/**
 * Subnets cell component for displaying subnet details
 */
const SubnetsCellComponent = ({ value }: { value: any[] }) => {
  return (
    <>
      {value?.map((subnet) => (
        <Text key={subnet.name} variant="body2">
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

export default {
  getSimpleNetworkColumns,
  getManagedNetworkColumns,
}
